import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { AppInputField, AppSelectField } from '@/form/FormUI';
import type { NodeFormProps } from '@/nodes';
import type { ReturnNodeType } from '@/nodes/types';
import { useSavedFormValues } from '@/state';
import { extractFieldErrors } from '@/util/extractFieldErrors';
import { zodResolver } from '@hookform/resolvers/zod';
import { ReturnDataSchema, type ReturnDataType } from '@rock/types';
import { type FC, useCallback, useEffect } from 'react';
import { type FieldErrors, useForm, useWatch } from 'react-hook-form';
import { toast } from 'sonner';
import type { z } from 'zod';

type FormInput = z.input<typeof ReturnDataSchema>;

export const ReturnForm: FC<NodeFormProps<ReturnNodeType>> = ({ flowId, node, updateNode, cancelUpdate }) => {
    const [formState, setFormState] = useSavedFormValues<FormInput>({ flowId, nodeId: node.id });
    const form = useForm<FormInput, unknown, ReturnDataType>({
        resolver: zodResolver(ReturnDataSchema),
        defaultValues: formState || { ...node.data, returnType: node.data.returnType ?? 'count' },
    });

    const watchedFields = useWatch({ control: form.control });
    useEffect(() => {
        setFormState(watchedFields);
    }, [setFormState, watchedFields]);

    const onSubmit = useCallback(
        (values: ReturnDataType) => {
            setFormState(null);
            updateNode({
                ...node,
                data: values,
            });
        },
        [setFormState, node, updateNode],
    );

    const onInvalid = useCallback((errors: FieldErrors) => {
        toast.error('Validation Errors', {
            description: extractFieldErrors(errors).join(', '),
        });
    }, []);

    const onCancel = useCallback(() => {
        setFormState(null);
        cancelUpdate();
    }, [setFormState, cancelUpdate]);

    return (
        <Form {...form}>
            <form
                onSubmit={form.handleSubmit(onSubmit, onInvalid)}
                className="flex flex-1 flex-col gap-8 items-stretch overflow-hidden"
            >
                <div className="flex flex-1 flex-col gap-8 items-stretch p-5 overflow-scroll">
                    <AppSelectField
                        control={form.control}
                        name="returnType"
                        label="Return Type"
                        options={[
                            { label: 'Count (fixed)', value: 'count' },
                            { label: 'Edge (dynamic)', value: 'edge' },
                        ]}
                    />

                    {watchedFields.returnType === 'count' && (
                        <AppInputField
                            label="Dismiss Screen Count"
                            control={form.control}
                            inputType="number"
                            name="dismissCount"
                            description="The number of screens to go backwards"
                        />
                    )}
                </div>

                <div className="flex flex-row gap-3 p-5">
                    <Button type="submit" className="flex-1">
                        Save Details
                    </Button>
                    <Button type="button" className="flex-1" variant="outline" onClick={onCancel}>
                        Cancel
                    </Button>
                </div>
            </form>
        </Form>
    );
};
